/**
 * The Access codes card on the artifact detail screen (f15.4), with its three
 * dialogs: Issue, the one-time Reveal, and Revoke. Mirrors the "Access codes"
 * panel, `codeModal` and `revokeModal` of `artifact-detail.html`.
 *
 * The plaintext code lives ONLY in the Reveal dialog's local state (held here
 * as `revealed`) and is dropped when the dialog closes. It never reaches the
 * TanStack Query cache (the issue mutation's result is the stored record, not
 * the code), the URL, storage or a log (BR-F15.4-04).
 *
 * Issue and Revoke render only with `artifact:publish`; the server re-checks
 * every call (BR-F15.4-10).
 */

import { useState } from 'react';
import { Ban, Check, Copy, Eye, KeyRound, TriangleAlert } from 'lucide-react';

import type { AccessPassword, ArtifactDetail } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { useArtifactAccessPasswords, useIssueAccessPassword, useRevokeAccessPassword } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import { defaultExpiry, isActive, redemptionsText, revokeTooltip, statusPill } from './accessCodeView';
import { formatDate } from './artifactView';

interface Revealed {
  label: string;
  plaintext: string;
}

function AccessCodeRow({
  password,
  mayPublish,
  onRevoke,
}: {
  password: AccessPassword;
  mayPublish: boolean;
  onRevoke: (password: AccessPassword) => void;
}) {
  const active = isActive(password);
  const tooltip = revokeTooltip(password);
  return (
    <tr>
      <td>
        <b>{password.label}</b>
      </td>
      {password.pinned_version ? (
        <td className="mono">v{password.pinned_version.version_number}</td>
      ) : (
        <td className="hint">follows current</td>
      )}
      <td>{password.issued_by.name}</td>
      <td>{password.expires_at ? formatDate(password.expires_at) : 'Never'}</td>
      <td className="num">{redemptionsText(password)}</td>
      <td>{password.last_used_at ? formatDate(password.last_used_at) : '—'}</td>
      <td>
        <span
          className={`badge ${statusPill(password.status.code)}`}
          title={password.revocation_reason ? `Revoked: ${password.revocation_reason}` : undefined}
        >
          {password.status.name}
        </span>
      </td>
      <td>
        {mayPublish && (
          <div className="row-actions">
            <button
              type="button"
              className={`btn btn-secondary btn-icon${active ? '' : ' is-disabled'}`}
              title={tooltip}
              aria-label={`Revoke ${password.label}`}
              disabled={!active}
              onClick={() => onRevoke(password)}
            >
              <Ban aria-hidden="true" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}

export function AccessCodesCard({ artifact, mayPublish }: { artifact: ArtifactDetail; mayPublish: boolean }) {
  const codes = useArtifactAccessPasswords(artifact.id);
  const [issuing, setIssuing] = useState(false);
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [revoking, setRevoking] = useState<AccessPassword | null>(null);
  const items = codes.data?.items ?? [];

  return (
    <div className="card">
      <div className="card-head">
        <h3>Access codes</h3>
        {mayPublish && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setIssuing(true)}>
            <KeyRound aria-hidden="true" /> Issue code
          </button>
        )}
      </div>
      <div className="card-body access-codes-body">
        <p className="hint mb-4">
          A code always opens exactly this artifact. Codes are stored hashed and shown once at creation &mdash; Atlas
          cannot display an existing code again.
        </p>
        {codes.isPending ? (
          <div aria-busy="true" aria-label="Loading access codes">
            {[0, 1].map((index) => (
              <span key={index} className="skeleton-bar group-skeleton-line" />
            ))}
          </div>
        ) : codes.error ? (
          <div className="alert alert-danger" role="alert">
            <TriangleAlert aria-hidden="true" />
            <div>
              <div className="a-title">Access codes could not be loaded</div>
              <div className="a-body">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => void codes.refetch()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <div className="e-icon">
              <KeyRound aria-hidden="true" />
            </div>
            <h3>No access codes yet</h3>
            <p>Issue one code per contact, so each can be revoked without disturbing the others.</p>
          </div>
        ) : (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Pinned version</th>
                  <th>Issued by</th>
                  <th>Expires</th>
                  <th className="num">Redemptions</th>
                  <th>Last used</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((password) => (
                  <AccessCodeRow key={password.id} password={password} mayPublish={mayPublish} onRevoke={setRevoking} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {issuing && (
        <IssueAccessCodeDialog
          artifact={artifact}
          onClose={() => setIssuing(false)}
          onIssued={(label, plaintext) => setRevealed({ label, plaintext })}
        />
      )}
      {revealed && <RevealAccessCodeDialog revealed={revealed} onDone={() => setRevealed(null)} />}
      {revoking && <RevokeAccessCodeDialog artifact={artifact} password={revoking} onClose={() => setRevoking(null)} />}
    </div>
  );
}

export function IssueAccessCodeDialog({
  artifact,
  onClose,
  onIssued,
}: {
  artifact: ArtifactDetail;
  onClose: () => void;
  onIssued: (label: string, plaintext: string) => void;
}) {
  const [label, setLabel] = useState('');
  const [pinned, setPinned] = useState('');
  const [expiresOn, setExpiresOn] = useState(() => defaultExpiry());
  const [maximum, setMaximum] = useState('');
  const issue = useIssueAccessPassword(artifact, (plaintext) => onIssued(label.trim(), plaintext));
  const failure = issue.error;

  function submit() {
    if (!label.trim()) return;
    issue.mutate(
      {
        label,
        pinned_version_id: pinned || null,
        expires_on: expiresOn || null,
        max_redemptions: maximum === '' ? null : Number(maximum),
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title="Issue access code"
      subtitle={artifact.title}
      onClose={onClose}
      onSubmit={submit}
      busy={issue.isPending}
      submitDisabled={!label.trim()}
      submitLabel="Generate code"
      submitIcon={<KeyRound aria-hidden="true" />}
      message={generalMessage(failure, 'label', 'pinned_version_id', 'expires_on', 'max_redemptions')}
    >
      <DialogField
        label="Label"
        htmlFor="code-label"
        span
        required
        hint="Required — it is the only way to tell codes apart later."
        problem={problemFor(failure, 'label')}
      >
        <input
          id="code-label"
          type="text"
          maxLength={120}
          value={label}
          placeholder="Blackstone CTO office"
          onChange={(event) => setLabel(event.target.value)}
        />
      </DialogField>
      <DialogField
        label="Pinned version"
        htmlFor="code-pinned"
        span
        problem={problemFor(failure, 'pinned_version_id')}
      >
        <select id="code-pinned" value={pinned} onChange={(event) => setPinned(event.target.value)}>
          <option value="">Follow the published version</option>
          {artifact.versions.map((version) => (
            <option key={version.id} value={version.id}>
              v{version.version_number}
            </option>
          ))}
        </select>
      </DialogField>
      <DialogField
        label="Expires"
        htmlFor="code-expires"
        hint="Clear for a code that never expires."
        problem={problemFor(failure, 'expires_on')}
      >
        <input id="code-expires" type="date" value={expiresOn} onChange={(event) => setExpiresOn(event.target.value)} />
      </DialogField>
      <DialogField
        label="Maximum redemptions"
        htmlFor="code-maximum"
        problem={problemFor(failure, 'max_redemptions')}
      >
        <input
          id="code-maximum"
          type="number"
          min={1}
          max={100000}
          step={1}
          value={maximum}
          placeholder="unlimited"
          onChange={(event) => setMaximum(event.target.value)}
        />
      </DialogField>
      <div className="alert alert-warning span-2">
        <Eye aria-hidden="true" />
        <div>
          <div className="a-title">Shown once</div>
          <div className="a-body">
            The code appears on the next screen and is never retrievable afterwards. Only its hash is stored.
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** Shows the plaintext exactly once. Only "Done": closing discards it. */
export function RevealAccessCodeDialog({ revealed, onDone }: { revealed: Revealed; onDone: () => void }) {
  const [copied, setCopied] = useState(false);

  function copy() {
    void navigator.clipboard?.writeText(revealed.plaintext).then(() => setCopied(true));
  }

  return (
    <div className="modal-scrim" role="presentation">
      <div className="modal narrow" role="dialog" aria-modal="true" aria-label="Access code issued">
        <div className="modal-head">
          <div>
            <h3>Access code issued</h3>
            <div className="m-sub">{revealed.label}</div>
          </div>
        </div>
        <div className="modal-body">
          <p className="hint mb-4">Give this code to {revealed.label}. It opens this artifact only.</p>
          <div className="access-code-reveal">
            <output className="mono access-code-value" aria-label="Access code">
              {revealed.plaintext}
            </output>
            <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />} {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="alert alert-warning mt-4">
            <TriangleAlert aria-hidden="true" />
            <div>
              <div className="a-title">This is the only time it is shown</div>
              <div className="a-body">
                Atlas stores only its hash. If it is lost, issue a new code and revoke this one.
              </div>
            </div>
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn btn-primary" onClick={onDone}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export function RevokeAccessCodeDialog({
  artifact,
  password,
  onClose,
}: {
  artifact: ArtifactDetail;
  password: AccessPassword;
  onClose: () => void;
}) {
  const revoke = useRevokeAccessPassword(artifact);
  const [reason, setReason] = useState('');
  const failure = revoke.error;
  return (
    <Modal
      title="Revoke code?"
      subtitle={password.label}
      onClose={onClose}
      onSubmit={() => {
        if (reason.trim()) revoke.mutate({ passwordId: password.id, reason }, { onSuccess: onClose });
      }}
      busy={revoke.isPending}
      submitDisabled={!reason.trim()}
      submitLabel="Revoke code"
      submitIcon={<Ban aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={generalMessage(failure, 'reason')}
    >
      <p className="hint mb-4">The code stops working immediately. Other codes on this artifact are unaffected.</p>
      <DialogField label="Reason" htmlFor="revoke-reason" required problem={problemFor(failure, 'reason')}>
        <textarea
          id="revoke-reason"
          className="publication-reason"
          maxLength={500}
          value={reason}
          placeholder="Required for revocation"
          onChange={(event) => setReason(event.target.value)}
        />
      </DialogField>
    </Modal>
  );
}
